import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import useAuthFetch from "../../../../hooks/useAuthFetch";
import PaginationControl from "../../../../components/Table/PaginationControl";
import TableHeader from "../../../../components/Table/TableHeader";
import TableRow from "../../../../components/Table/TableRow";
import Avatar from "../../../../components/Images/Avatar";
import { FaExclamationTriangle, FaStar } from "react-icons/fa";
import useLocalStorage from "../../../../hooks/useLocalStorage";
import useUpdate from "../../../../hooks/useUpdate";
import SearchPanel, { initialState } from "../components/SearchPanel";

const placeHeaders = {
  Id: "ID",
  Name: "NAME",
  Status: "STATUS",
  Country: "COUNTRY",
  City: "CITY",
  Claims: "CLAIMS",
  Rating: "RATING",
  Business: "BUSINESS",
  Reviews: "REVIEWS",
  Author: "AUTHOR",
};

const Places = () => {
  let { getItem, setItem } = useLocalStorage(
    "placesSearchPreferences",
    initialState,
  );
  const navigate = useNavigate();
  const authFetch = useAuthFetch();

  const [searchSetting, setSearchSetting] = useState(initialState);

  useEffect(() => {
    const item = getItem();
    if (item && item !== initialState) {
      setSearchSetting(item);
    }
  }, [getItem]);

  useUpdate(() => {
    if (searchSetting !== getItem()) {
      setItem(searchSetting);
    }
  }, [searchSetting]);

  const placesUrl = `/v1/admin/places?page=${searchSetting.page}&pageSize=${searchSetting.size}&sortBy=${searchSetting.sortBy}&order=${searchSetting.sortDirection}&status=${searchSetting.status}&searchPattern=${searchSetting.searchPattern}&onlyWithReports=${searchSetting.onlyWithReports}&userId=${searchSetting.userId}&city=${searchSetting.city}&countryCode=${searchSetting.countryCode}&tag=${searchSetting.tag}&category=${searchSetting.category}&type=${searchSetting.type}`;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["places", placesUrl],
    queryFn: () => authFetch(placesUrl),
    placeholderData: keepPreviousData,
  });

  return (
    <div>
      <div className="flex justify-between mb-6 items-center">
        <span className="text-lg">ACTIVE PLACES</span>
      </div>
      <div className="p-6 border grid gap-6">
        <SearchPanel
          setSearchSetting={setSearchSetting}
          searchSetting={searchSetting}
        />
        <div className="mb-6">
          <table className="w-full">
            <thead className="text-gray-700 text-xs bg-gray-100 ">
              <tr>
                {Object.entries(placeHeaders).map(([key, value]) => (
                  <TableHeader name={value} />
                ))}
              </tr>
            </thead>
            {isLoading ? (
              <tbody>
                <tr>
                  <td
                    colSpan={Object.keys(placeHeaders).length}
                    className="text-center py-8"
                  >
                    Loading...
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody
                className={isFetching ? "opacity-50 transition-opacity" : ""}
              >
                {data?.data?.map((place, i) => (
                  <tr
                    key={i}
                    onClick={() => navigate(`/places/actives/${place.id}`)}
                    className="border-b text-xs hover:bg-gray-50 duration-300 hover:cursor-pointer"
                  >
                    <TableRow value={place.id} />
                    <TableRow
                      value={
                        <div className="flex items-center gap-2">
                          <Avatar image={place.photos[0]} />
                          <div>{place.name}</div>
                          {place.aiDiscovered && <AiBadge />}
                        </div>
                      }
                    />
                    <TableRow
                      value={
                        <div className="flex items-center gap-2">
                          <span
                            className={`p-1 rounded-full ${place.isBlocked ? "bg-yellow-500" : "bg-lime-500"}`}
                          />
                          {place.isBlocked ? "Blocked" : "Active"}
                        </div>
                      }
                    />
                    <TableRow value={place.country} />
                    <TableRow value={place.city} />
                    <TableRow
                      value={
                        place.reports.length !== 0 && (
                          <div className="flex gap-1 items-center">
                            <FaExclamationTriangle className="text-red-600" />
                            {place.reports.length}
                          </div>
                        )
                      }
                    />
                    <TableRow
                      value={
                        <div className="flex items-center gap-2">
                          {[...Array(5)].map((_, index) => (
                            <span
                              key={index}
                              className={`${place.rating > index ? "text-yellow-500" : "text-gray-300"}`}
                            >
                              <FaStar />
                            </span>
                          ))}
                        </div>
                      }
                    />
                    <TableRow
                      value={<div>{place.businessOwner && "YES"}</div>}
                    />
                    <TableRow value={place.reviewCount} />
                    <TableRow
                      value={`${place.createdBy.firstName} ${place.createdBy.lastName}`}
                    />
                  </tr>
                ))}
              </tbody>
            )}
          </table>
          {data?.pageMetadata ? (
            <PaginationControl
              searchSetting={searchSetting}
              setSearchSetting={setSearchSetting}
              paginationSettings={data.pageMetadata}
            />
          ) : (
            !isLoading && <span>Loading...</span>
          )}
        </div>
      </div>
    </div>
  );
};

const AiBadge = () => (
  <span className="text-[10px] bg-lime-100 text-lime-700 px-1.5 py-0.5 rounded font-medium whitespace-nowrap">
    AI
  </span>
);

export default Places;
